import React from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectConceptCache, selectLensEditorLens, updateDraft, updateRule } from "redux/slices/lensEditor";
import { EntityType, LensRule } from "generated/graphql";
import { umlsToEntityType } from "Backend";
import { ReadOnlyMemo } from "routes/lens-editor/ReadonlyRule";
import { RuleEditor } from "routes/lens-editor/RuleEditor";

interface Props {
  rule: LensRule;
  readOnly?: boolean;
  linkToDuplicates?: boolean;
  hideConceptButton?: boolean;
}

export const RuleMode = (props: Props) => {
  const [edit, setEdit] = React.useState(false);
  const handleSetEditMode = React.useCallback(() => setEdit(true), [setEdit]);

  const dispatch = useAppDispatch();
  const { rule, readOnly, linkToDuplicates, hideConceptButton } = props;
  const { ruleId, entityQuery } = rule;
  const entityId = entityQuery?.entityId;

  const lensId = useAppSelector(selectLensEditorLens).id;
  const conceptCache = useAppSelector(selectConceptCache);

  const concept = entityId ? conceptCache[entityId] : undefined;

  const deleteRule = React.useCallback(() => {
    dispatch(updateDraft({ lensId, rules: [], deletes: [ruleId] }));
  }, [dispatch, ruleId]);
  const doUpdateRule = React.useCallback(
    (updateFn: (rule: LensRule) => void) => {
      dispatch(updateRule({ lensId, ruleId, updateFn }));
    },
    [dispatch, lensId, ruleId]
  );

  const ruleTypeLower = rule.entityQuery?.entityType.toLowerCase();
  const mismatchType =
    concept !== undefined && concept !== null && concept.umlsEntityType.toLowerCase() !== ruleTypeLower;
  const actualType = concept?.umlsEntityType;
  React.useEffect(() => {
    if (mismatchType && actualType) {
      doUpdateRule((old) => {
        old.entityQuery = {
          entityType: umlsToEntityType(actualType)! as EntityType,
          entityId: old.entityQuery!.entityId,
        };
      });
    }
  }, [entityId]);

  if (readOnly || !edit) {
    return (
      <ReadOnlyMemo
        rule={rule}
        readOnly={readOnly}
        setSelected={handleSetEditMode}
        concept={concept}
        deleteRule={deleteRule}
        linkToDuplicates={linkToDuplicates}
        hideConceptButton={hideConceptButton}
      />
    );
  } else {
    return <RuleEditor rule={rule} lensId={lensId} onClose={() => setEdit(false)} key={JSON.stringify(rule)} />;
  }
};
